// docs/52: hidden things and lair traps on the board. A trap the party has not seen through is
// drawn as its guise — a few scuffs, sticks, leaves or a soot smudge on the ground, faint enough
// to pass over and plain enough to notice — never as the trap. Seen for what it is, it is the
// trap itself (Oryx spikes 429, pit 434, jaws 433, skull rune 432) with red corner ticks while
// armed, dimmed once spent or made safe. A found hidden way is a door the tiler already draws (its
// square is `Terrain::Door`; once open, the gap's ground); this layer brackets it in gold so the
// party knows it was a secret, and a shortcut gets a gold arrow out of each side it joins, so it
// reads as a way through to the far side and not a hole to go down. A found clue is a scroll on a mound of turned earth. docs/76: a found hidden door into
// a hidden room is bracketed the same way; a hidden stairway is a stone slab set in its rock
// (the stair down, once open) and the cellar it leads to is laid with flagstones over the field's
// own ground, so it reads as a room below and not a clearing.
import Phaser from 'phaser';
import type { FixtureView, Snapshot, Tile, TrapKindName } from '../../core/sim/types';
import { PUZZLE_ATLAS } from './puzzleArt';
import { oneShot } from './oneShots';

// docs/60 DC9: a known chute is the open shaft with its iron rim (580), not the pit
const TRAP_FRAMES: Record<TrapKindName, string> = { Darts: 'world_0429', Pit: 'world_0434', Jaws: 'world_0433', Rune: 'world_0432', Chute: 'world_0580' };
const SCROLL = 'item_136';
const STAIR_DOWN = 'world_0067';
const SLAB = 0x3a3530, FLAG = 0x2a2622, JOINT = 0x15120f;
const GOLD = 0xffd166, DANGER = 0xff5a4f, SOOT = 0x1d1610, EARTH = 0x5a4128;

/** A small seeded jitter per fixture, so two guises of a kind never look stamped. */
function jitter(id: number, k: number): number {
  const h = Math.imul(id * 2654435761 + k * 40503, 0x9e3779b1) >>> 0;
  return ((h >>> 8) % 1000) / 1000;
}

export class SecretLayer {
  private objects: Phaser.GameObjects.GameObject[] = [];

  /** `floor`: the layer the cellar's flagstones go in, under every fixture and stash (the ground's
   *  own, by default this layer). */
  constructor(private readonly scene: Phaser.Scene, private readonly layer: Phaser.GameObjects.Layer, private readonly cell: number, private readonly floor: Phaser.GameObjects.Layer = layer) {}

  private add<T extends Phaser.GameObjects.GameObject>(o: T): T {
    this.layer.add(o);
    this.objects.push(o);
    return o;
  }

  private has(atlas: string, frame: string): boolean {
    return this.scene.textures.exists(atlas) && this.scene.textures.get(atlas).has(frame);
  }

  draw(fixtures: readonly FixtureView[] | undefined, board?: Snapshot['board']): void {
    for (const o of this.objects) o.destroy();
    this.objects = [];
    for (const f of fixtures ?? []) {
      if (f.kind === 'Trap') this.trap(f);
      else if (f.kind === 'Passage') { this.way(f); if (!f.nook && board) this.through(f, board); }
      else if (f.kind === 'Clue') this.clue(f);
      else if (f.kind === 'HiddenDoor') this.door(f);
    }
  }

  private trap(f: FixtureView): void {
    const c = this.cell, x = f.tile.x * c, y = f.tile.y * c;
    if (!f.known) { this.guise(f); return; }
    const frame = TRAP_FRAMES[f.trap ?? 'Darts'];
    const live = !!f.armed;
    if (this.has('world', frame)) {
      const img = this.add(this.scene.add.image(x, y, 'world', frame).setOrigin(0, 0));
      // a sprung pit (or chute) stays an open hole; anything else spent or made safe fades into the ground
      img.setAlpha(live ? 1 : f.trap === 'Pit' || f.trap === 'Chute' ? 0.8 : 0.45);
      if (!live) img.setTint(0x9a9a9a);
    } else {
      const g = this.add(this.scene.add.graphics());
      g.lineStyle(1.5, live ? DANGER : 0x8a8f99, live ? 1 : 0.6).strokeCircle(x + c / 2, y + c / 2, c * 0.32);
    }
    if (live) {
      // red corner ticks: seen, armed, step round it or send the rogue
      const g = this.add(this.scene.add.graphics());
      const k = Math.max(3, Math.round(c * 0.2)), p = 1.5;
      g.lineStyle(1, DANGER, 0.95);
      for (const [cx, cy, dx, dy] of [[x + p, y + p, 1, 1], [x + c - p, y + p, -1, 1], [x + p, y + c - p, 1, -1], [x + c - p, y + c - p, -1, -1]] as const) {
        g.beginPath(); g.moveTo(cx + dx * k, cy); g.lineTo(cx, cy); g.lineTo(cx, cy + dy * k); g.strokePath();
      }
    }
  }

  /** The guise: not hidden, only not obvious. Marks by what the trap is, on a faint dark patch. */
  private guise(f: FixtureView): void {
    const c = this.cell, x = f.tile.x * c, y = f.tile.y * c;
    const r = (k: number) => jitter(f.id, k);
    // the disturbed patch: the commissioned piece where there is one, under the marks
    if (this.has(PUZZLE_ATLAS.key, 'guise')) {
      this.add(this.scene.add.image(x, y, PUZZLE_ATLAS.key, 'guise').setOrigin(0, 0).setDisplaySize(c, c).setAlpha(0.55));
    }
    const g = this.add(this.scene.add.graphics());
    if (!this.has(PUZZLE_ATLAS.key, 'guise')) g.fillStyle(SOOT, 0.16).fillEllipse(x + c * (0.45 + r(1) * 0.1), y + c * (0.55 + r(2) * 0.1), c * 0.78, c * 0.5);
    switch (f.trap) {
      case 'Darts': {
        // a scuffed threshold: three short pale scratches across the step
        g.lineStyle(1, 0xd8c7a0, 0.5);
        for (let i = 0; i < 3; i++) {
          const sx = x + c * (0.25 + i * 0.2 + r(3 + i) * 0.06), sy = y + c * (0.4 + r(6 + i) * 0.2);
          g.beginPath(); g.moveTo(sx, sy); g.lineTo(sx + c * 0.12, sy + c * 0.1); g.strokePath();
        }
        break;
      }
      case 'Pit': {
        // loose boughs, reeds or a drift: a few crossed sticks
        g.lineStyle(1, 0x6b4a2b, 0.6);
        for (let i = 0; i < 4; i++) {
          const a = (i * Math.PI) / 4 + r(10 + i) * 0.5;
          const cx = x + c / 2 + (r(20 + i) - 0.5) * c * 0.2, cy = y + c * 0.55 + (r(30 + i) - 0.5) * c * 0.15;
          const dx = Math.cos(a) * c * 0.28, dy = Math.sin(a) * c * 0.14;
          g.beginPath(); g.moveTo(cx - dx, cy - dy); g.lineTo(cx + dx, cy + dy); g.strokePath();
        }
        break;
      }
      case 'Chute': {
        // docs/60 DC9: a cracked flagstone — a square outline sunk a hair, and a crack across it
        const x0 = x + c * 0.18, y0 = y + c * 0.2, s = c * 0.64;
        g.lineStyle(1, SOOT, 0.45).strokeRect(x0, y0, s, s);
        g.lineStyle(1, 0x0d0a08, 0.55);
        g.beginPath(); g.moveTo(x0 + s * (0.1 + r(60) * 0.2), y0); g.lineTo(x0 + s * 0.45, y0 + s * (0.4 + r(61) * 0.2)); g.lineTo(x0 + s * (0.7 + r(62) * 0.2), y0 + s); g.strokePath();
        g.beginPath(); g.moveTo(x0 + s * 0.45, y0 + s * 0.5); g.lineTo(x0 + s, y0 + s * (0.3 + r(63) * 0.3)); g.strokePath();
        break;
      }
      case 'Jaws': {
        // a drift of leaves: small brown and olive ovals heaped on the path
        for (let i = 0; i < 6; i++) {
          g.fillStyle(i % 2 ? 0x8a6a2a : 0x5e7a2c, 0.6);
          g.fillEllipse(x + c * (0.25 + r(40 + i) * 0.5), y + c * (0.35 + r(50 + i) * 0.35), c * 0.16, c * 0.1);
        }
        break;
      }
      default: {
        // a scorched flagstone: a soot ring and a darker heart
        g.lineStyle(1, SOOT, 0.4).strokeCircle(x + c / 2, y + c * 0.55, c * 0.26);
        g.fillStyle(SOOT, 0.22).fillCircle(x + c / 2, y + c * 0.55, c * 0.14);
      }
    }
  }

  /** A found hidden way: the tiler draws its door; gold brackets say it was a secret. */
  private way(f: FixtureView): void {
    const c = this.cell, x = f.tile.x * c, y = f.tile.y * c;
    const g = this.add(this.scene.add.graphics());
    const k = Math.max(3, Math.round(c * 0.25)), p = 1;
    g.lineStyle(1, GOLD, f.on ? 0.45 : 0.85);
    for (const [cx, cy, dx, dy] of [[x + p, y + p, 1, 1], [x + c - p, y + p, -1, 1], [x + p, y + c - p, 1, -1], [x + c - p, y + c - p, -1, -1]] as const) {
      g.beginPath(); g.moveTo(cx + dx * k, cy); g.lineTo(cx, cy); g.lineTo(cx, cy + dy * k); g.strokePath();
    }
  }

  /** A shortcut's two open sides (N–S or W–E, whichever both walk), a gold arrow pointing out of each. */
  private through(f: FixtureView, board: Snapshot['board']): void {
    const walks = (x: number, y: number): boolean => {
      if (x < 0 || y < 0 || x >= board.w || y >= board.h) return false;
      const t = board.tiles[y * board.w + x];
      return !!t && !['Wall', 'Void', 'Deep', 'Chasm'].includes(t.terrain) && !(t.terrain === 'Door' && !t.doorOpen);
    };
    const { x: tx, y: ty } = f.tile;
    const ns = walks(tx, ty - 1) && walks(tx, ty + 1);
    if (!ns && !(walks(tx - 1, ty) && walks(tx + 1, ty))) return;
    const c = this.cell, mx = tx * c + c / 2, my = ty * c + c / 2;
    const g = this.add(this.scene.add.graphics());
    const r = c * 0.36, k = Math.max(3, c * 0.14);
    g.lineStyle(2, GOLD, f.on ? 0.8 : 0.55);
    for (const s of [-1, 1]) {
      // the tip just short of the square's edge, the arms back toward the middle
      const [px, py] = ns ? [mx, my + s * r] : [mx + s * r, my];
      g.beginPath();
      if (ns) { g.moveTo(px - k, py - s * k); g.lineTo(px, py); g.lineTo(px + k, py - s * k); }
      else { g.moveTo(px - s * k, py - k); g.lineTo(px, py); g.lineTo(px - s * k, py + k); }
      g.strokePath();
    }
  }

  /** docs/76 EE3: a found hidden door. Into a hidden room, the tiler draws the door and this only
   *  brackets it; a stairway down is a slab in the rock, the stair once open, and its cellar is
   *  flagged. */
  private door(f: FixtureView): void {
    if (f.below === undefined) { this.way(f); return; }
    const c = this.cell, x = f.tile.x * c, y = f.tile.y * c;
    for (const t of f.room ?? []) this.flagstone(t);
    if (f.on && this.has('world', STAIR_DOWN)) {
      this.add(this.scene.add.image(x, y, 'world', STAIR_DOWN).setOrigin(0, 0));
    } else if (!f.on && this.has(PUZZLE_ATLAS.key, 'trapdoor')) {
      this.add(this.scene.add.image(x, y, PUZZLE_ATLAS.key, 'trapdoor').setOrigin(0, 0).setDisplaySize(c, c));
    } else {
      const g = this.add(this.scene.add.graphics());
      g.fillStyle(f.on ? 0x0b0a09 : SLAB, 1).fillRect(x + 2, y + 2, c - 4, c - 4);
      g.lineStyle(1, 0x7a7066, 0.9).strokeRect(x + 2.5, y + 2.5, c - 5, c - 5);
      if (!f.on) {
        // an iron ring to lift it by
        g.lineStyle(1, 0xb9a98c, 0.95).strokeCircle(x + c / 2, y + c * 0.58, c * 0.12);
      }
    }
    this.way(f);
  }

  /** A square of a cellar: dark flagstones laid over whatever the field's kit drew there. */
  private flagstone(t: Tile): void {
    const c = this.cell, x = t.x * c, y = t.y * c;
    const g = this.scene.add.graphics();
    this.floor.add(g);
    this.objects.push(g);
    g.fillStyle(FLAG, 0.92).fillRect(x, y, c, c);
    g.lineStyle(1, JOINT, 0.8);
    const off = (t.y % 2) * (c / 2);
    g.beginPath(); g.moveTo(x, y + c / 2); g.lineTo(x + c, y + c / 2); g.strokePath();
    for (const k of [0, 1]) {
      const vx = x + ((off + k * c / 2) % c), y0 = y + k * (c / 2);
      g.beginPath(); g.moveTo(vx, y0); g.lineTo(vx, y0 + c / 2); g.strokePath();
    }
  }

  /** A found clue: turned earth and the scrap of map lying on it. */
  private clue(f: FixtureView): void {
    const c = this.cell, x = f.tile.x * c, y = f.tile.y * c;
    if (this.has(PUZZLE_ATLAS.key, 'clue')) {
      this.add(this.scene.add.image(x, y, PUZZLE_ATLAS.key, 'clue').setOrigin(0, 0).setDisplaySize(c, c));
      return;
    }
    const g = this.add(this.scene.add.graphics());
    g.fillStyle(EARTH, 0.55).fillEllipse(x + c / 2, y + c * 0.68, c * 0.7, c * 0.32);
    if (this.has('items', SCROLL)) this.add(this.scene.add.image(x + c / 2, y + c * 0.55, 'items', SCROLL).setScale(0.75));
    else g.fillStyle(0xe9dcc0, 1).fillRect(x + c * 0.3, y + c * 0.4, c * 0.4, c * 0.22);
  }

  /** docs/52: a ring of light opening out from a square as something there shows itself. */
  glint(t: Tile, ms: number, colour = GOLD): void {
    const c = this.cell;
    const g = oneShot(this.scene, this.scene.add.graphics());
    this.layer.add(g);
    const st = { r: c * 0.2, a: 1 };
    const paint = () => { g.clear(); g.lineStyle(2, colour, st.a).strokeCircle(t.x * c + c / 2, t.y * c + c / 2, st.r); };
    paint();
    this.scene.tweens.add({ targets: st, r: c * 0.8, a: 0, duration: Math.max(200, ms), ease: 'Quad.easeOut', onUpdate: paint, onComplete: () => g.destroy() });
  }

  /** docs/52: a trap snaps — a red ring, quicker and tighter than a find. */
  snap(t: Tile, ms: number): void { this.glint(t, ms * 0.8, DANGER); }
}
